import { CheckCircle2, Download } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { downloadGuestPlatterOrderConfirmation } from "../api/orders";
import { Button } from "../components/ui/button";
import { formatCurrency } from "../utils/format";

export function SuccessPage() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [downloadError, setDownloadError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const pickupLocationLabel = state?.pickupLocation || "-";
  const orderNumber = state?.orderId || "";
  const hasEmail = Boolean(state?.email);

  function handleContinueShopping() {
    navigate("/", { replace: true });
  }

  async function handleDownloadConfirmation() {
    if (!orderNumber) {
      setDownloadError("Order number is missing.");
      return;
    }

    try {
      setDownloading(true);
      setDownloadError("");
      const response = await downloadGuestPlatterOrderConfirmation(orderNumber);
      const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `OrderConfirmation-${orderNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError("Order confirmation could not be downloaded.");
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="glass-panel mx-auto max-w-2xl px-6 py-10 text-center sm:px-10 sm:py-14">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary text-primary">
        <CheckCircle2 className="h-8 w-8" />
      </div>
      <p className="mt-6 text-sm font-semibold uppercase tracking-[0.24em] text-muted-foreground">
        Order success
      </p>
      <h1 className="mt-3 font-display text-4xl font-semibold">Your platter order is confirmed.</h1>
      <p className="mt-4 text-base leading-7 text-muted-foreground">
        Order placed successfully.
      </p>
      {!hasEmail ? (
        <div className="mt-5 rounded-2xl border border-primary/25 bg-secondary/70 px-4 py-3 text-sm font-semibold text-foreground">
          Please download your order confirmation for your records.
        </div>
      ) : null}

      <div className="mt-8 grid gap-4 text-left sm:grid-cols-2">
        <div className="rounded-[1.5rem] border border-border bg-background/70 p-5">
          <p className="text-sm text-muted-foreground">Order reference</p>
          <p className="mt-2 text-lg font-semibold">Order Number: {orderNumber || "Created by API"}</p>
        </div>
        <div className="rounded-[1.5rem] border border-border bg-background/70 p-5">
          <p className="text-sm text-muted-foreground">Pickup location</p>
          <p className="mt-2 text-lg font-semibold">{pickupLocationLabel}</p>
        </div>
        <div className="rounded-[1.5rem] border border-border bg-background/70 p-5">
          <p className="text-sm text-muted-foreground">Pickup date</p>
          <p className="mt-2 text-lg font-semibold">{state?.pickupDate || "-"}</p>
        </div>
        <div className="rounded-[1.5rem] border border-border bg-background/70 p-5">
          <p className="text-sm text-muted-foreground">Amount paid</p>
          <p className="mt-2 text-lg font-semibold">{formatCurrency(state?.amount || 0)}</p>
        </div>
      </div>

      {downloadError ? (
        <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {downloadError}
        </div>
      ) : null}

      <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
        <Button
          disabled={downloading || !orderNumber}
          onClick={handleDownloadConfirmation}
          size="lg"
          type="button"
        >
          <Download className="h-4 w-4" />
          {downloading ? "Preparing..." : "Download Order Confirmation"}
        </Button>
        <Button onClick={handleContinueShopping} size="lg" type="button" variant="secondary">
          Continue Shopping
        </Button>
      </div>
    </div>
  );
}
